import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatPermissionDuration } from '../lib/exportUtils';
import {
  CheckCircle, XCircle, RotateCcw, Clock, Paperclip, Wallet, X,
  Loader2, Inbox, User, CalendarDays, IndianRupee, History, Search,
  Filter, CheckSquare, Square, AlertTriangle, CheckCircle2, Building2,
  UserCheck, Layers, Calendar, ChevronDown, ChevronUp, RefreshCw,
  Download, Eye, FileDown, FileText, MapPin, Gauge, Edit3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_FILTERS = [
  { key: '', label: 'All Types' },
  { key: 'local_conveyance', label: 'Local Conveyance' },
  { key: 'outstation_conveyance', label: 'Outstation Conveyance' },
  { key: 'loan', label: 'Advance / Loan' },
  { key: 'claim_sheet', label: 'Claim Sheet' },
  { key: 'reimbursement', label: 'Reimbursement' },
  { key: 'regularization', label: 'Regularization' },
  { key: 'leave', label: 'Leave' },
  { key: 'permission', label: 'Permission' },
  { key: 'ot', label: 'OT' },
  { key: 'shift', label: 'Shift' },
  { key: 'compoff', label: 'Comp-Off' },
];

const STATUS_TABS = [
  { key: 'my_actions', label: 'All Pending' },
  { key: 'pending', label: 'My Action Required' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'sent_back', label: 'Sent Back' },
];

const STATUS_CHIP = {
  pending: 'bg-amber-100 text-amber-700',
  pending_hr: 'bg-orange-100 text-orange-700',
  in_progress: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  rework: 'bg-purple-100 text-purple-700',
  sent_back: 'bg-purple-100 text-purple-700',
  my_actions: 'bg-blue-100 text-blue-700',
};

const ACTION_CHIP = {
  Approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Rejected: 'bg-red-50 text-red-700 border-red-200',
  'Sent Back': 'bg-purple-50 text-purple-700 border-purple-200',
  Forwarded: 'bg-blue-50 text-blue-700 border-blue-200',
  Pending: 'bg-amber-50 text-amber-700 border-amber-200',
};

const STATUS_COLORS = {
  ...STATUS_CHIP,
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  pending_hr: 'bg-orange-50 text-orange-700 border-orange-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  rework: 'bg-purple-50 text-purple-700 border-purple-200',
  sent_back: 'bg-purple-50 text-purple-700 border-purple-200',
  my_actions: 'bg-blue-50 text-blue-700 border-blue-200',
  Pending: 'bg-amber-50 text-amber-700 border-amber-200',
  Approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Rejected: 'bg-red-50 text-red-700 border-red-200',
  'Sent Back': 'bg-purple-50 text-purple-700 border-purple-200',
  Rework: 'bg-purple-50 text-purple-700 border-purple-200',
  Forwarded: 'bg-blue-50 text-blue-700 border-blue-200',
  'In Progress': 'bg-amber-50 text-amber-700 border-amber-200',
};

const AMOUNT_EDIT_TYPES = ['loan', 'claim_sheet', 'reimbursement', 'outstation_conveyance'];

const fmtAmt = (v) => (v === null || v === undefined || v === '') ? '—'
  : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const summaryText = (row) => {
  const s = row.summary || {};
  const bits = [];
  if (s.amount !== undefined && s.amount !== null) bits.push(fmtAmt(s.amount));
  if (row.approval_type === 'permission' || s.duration_display) {
    bits.push(s.duration_display || (s.duration_minutes ? formatPermissionDuration(s.duration_minutes) : (s.hours ? `${s.hours} hrs` : '1 hour')));
  } else if (s.days !== undefined && s.days !== null) {
    if (s.is_half_day || s.days === 0.5) {
      bits.push(`0.5 Day ${s.half_day_period ? `(${s.half_day_period === 'first_half' ? 'First Half' : 'Second Half'})` : '(Half Day)'}`);
    } else {
      bits.push(`${s.days} day${s.days > 1 ? 's' : ''}`);
    }
  }
  if (row.approval_type !== 'permission' && !s.duration_display && s.hours) bits.push(`${s.hours} hrs`);
  if (s.period) bits.push(s.period);
  if (s.detail) bits.push(s.detail);
  return bits.filter(Boolean).join(' · ') || '—';
};

const DetailRow = ({ label, value }) => {
  const safeValue = (() => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return '—';
      }
    }
    return value;
  })();

  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-slate-50 last:border-0">
      <span className="text-xs text-slate-500 shrink-0">{label}</span>
      <span className="text-xs font-medium text-slate-800 text-right break-words">{safeValue}</span>
    </div>
  );
};

const formatKey = (key) => {
  if (!key) return '';
  const acronyms = {
    pan: 'PAN', uan: 'UAN', esi: 'ESI', pf: 'PF', ot: 'OT',
    emi: 'EMI', gstr: 'GSTR', gst: 'GST', id: 'ID', ctc: 'CTC',
    nps: 'NPS', lop: 'LOP', rlop: 'RLOP',
  };
  return key
    .split('_')
    .map(word => acronyms[word.toLowerCase()] || (word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');
};

const getDisplayableFields = (item) => {
  if (!item || typeof item !== 'object') return [];
  const skipKeys = new Set([
    '_id', 'id', 'tenant_id', 'company_id', 'employee_id',
    'workflow_id', 'approval_id', 'status', 'current_level',
    'history', 'created_at', 'updated_at', 'created_by',
    'updated_by', 'is_active', 'is_deleted', 'employee_name',
    'employee_code', 'department', 'designation', 'company',
    'approver_user_ids', 'level_name'
  ]);
  
  return Object.entries(item).filter(([key, val]) => {
    if (skipKeys.has(key)) return false;
    if (val === null || val === undefined || val === '') return false;
    if (typeof val === 'object') return false;
    return true;
  });
};

const getLocalConveyanceDetails = (dItem, kmInput, overrideActive, overrideInput) => {
  const origKm = Number(dItem?.original_km ?? dItem?.total_km ?? 0);

  // Policy Rate Resolution
  const snapRate = dItem?.policy_snapshot?.rate_per_km;
  let rate = (snapRate !== undefined && snapRate !== null && snapRate !== '')
    ? Number(snapRate)
    : Number(dItem?.rate_per_km || 0);
  if (rate === 0 && origKm > 0) {
    const convAmt = Number(dItem?.conveyance_amount || 0);
    if (convAmt > 0) {
      rate = Number((convAmt / origKm).toFixed(4));
    }
  }

  // KM Parsing
  let currentKm = origKm;
  let isKmInvalid = false;
  if (kmInput !== '' && kmInput !== null && kmInput !== undefined) {
    const parsed = Number(kmInput);
    if (isNaN(parsed) || parsed < 0) {
      isKmInvalid = true;
    } else {
      currentKm = parsed;
    }
  }

  // Conveyance Fare
  let conveyanceFare = 0;
  if (rate > 0 || currentKm !== origKm) {
    conveyanceFare = Number((currentKm * rate).toFixed(2));
  } else {
    conveyanceFare = Number(dItem?.conveyance_amount || 0);
  }

  // Other Expenses
  const exp = dItem?.expenses || {};
  const food = Number(dItem?.food_amount || exp.food_expense || 0);
  const parkingToll = Number(dItem?.parking_toll_amount || ((exp.parking || 0) + (exp.toll || 0)) || 0);
  const other = Number(dItem?.other_amount || exp.other_expense || 0);
  const otherExpenses = Number((food + parkingToll + other).toFixed(2));

  // Policy-Calculated Total
  const policyCalculatedTotal = Number((conveyanceFare + otherExpenses).toFixed(2));

  // Explicit Override Amount
  let isOverrideInvalid = false;
  let finalAmount = policyCalculatedTotal;
  if (overrideActive) {
    if (overrideInput !== '' && overrideInput !== null && overrideInput !== undefined) {
      const parsedOverride = Number(overrideInput);
      if (isNaN(parsedOverride) || parsedOverride < 0) {
        isOverrideInvalid = true;
      } else {
        finalAmount = Number(parsedOverride.toFixed(2));
      }
    } else {
      isOverrideInvalid = true;
    }
  }

  const isKmChanged = Number(currentKm.toFixed(2)) !== Number(origKm.toFixed(2));

  return {
    origKm,
    currentKm,
    isKmInvalid,
    isKmChanged,
    rate,
    conveyanceFare,
    food,
    parkingToll,
    other,
    otherExpenses,
    policyCalculatedTotal,
    isOverrideInvalid,
    finalAmount,
  };
};

export const ManagerApprovalInbox = ({ embedded = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [statusTab, setStatusTab] = useState(() => {
    const q = new URLSearchParams(window.location.search).get('tab');
    if (q && ['my_actions', 'pending', 'approved', 'rejected', 'sent_back'].includes(q.toLowerCase())) {
      return q.toLowerCase();
    }
    return 'my_actions';
  });
  const [typeFilter, setTypeFilter] = useState('');

  // Enhanced Filter States
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [designationFilter, setDesignationFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Selection States: map of `${row.approval_type}::${row.id}` -> row object
  const [selectedMap, setSelectedMap] = useState({});

  // Single Detail Drawer / Modal state
  const [detail, setDetail] = useState(null);           // { row, data|null }
  const [detailLoading, setDetailLoading] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [acting, setActing] = useState(false);

  // Local Conveyance KM & Amount Override State
  const [localConveyanceKm, setLocalConveyanceKm] = useState('');
  const [isAmountOverride, setIsAmountOverride] = useState(false);
  const [localConveyanceOverrideAmount, setLocalConveyanceOverrideAmount] = useState('');
  const [localConveyanceOverrideReason, setLocalConveyanceOverrideReason] = useState('');

  // Bulk Action Modals State
  const [bulkConfirmModal, setBulkConfirmModal] = useState({ open: false, action: 'approve' });
  const [bulkRemarks, setBulkRemarks] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkResultModal, setBulkResultModal] = useState({ open: false, data: null });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const params = { status: statusTab };
      if (typeFilter) params.approval_type = typeFilter;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (departmentFilter.trim()) params.department = departmentFilter.trim();
      if (designationFilter.trim()) params.designation = designationFilter.trim();
      if (levelFilter.trim()) params.approval_level = levelFilter.trim();

      const res = await axios.get(`${API_URL}/manager/approvals`, { params, withCredentials: true });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setFetchError('Unable to load approvals. Please try again.');
      toast.error('Failed to load approvals');
    }
    setLoading(false);
  }, [statusTab, typeFilter, searchQuery, fromDate, toDate, departmentFilter, designationFilter, levelFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Clear selection when filters or status tab change
  useEffect(() => {
    setSelectedMap({});
  }, [statusTab, typeFilter, searchQuery, fromDate, toDate, departmentFilter, designationFilter, levelFilter]);

  // Selection Helper Logic
  const isActionableTab = statusTab === 'pending' || statusTab === 'my_actions';
  const selectableRows = rows.filter(r => isActionableTab || ['pending', 'pending_hr', 'in_progress', 'submitted'].includes((r.status || '').toLowerCase()));
  const isRowSelected = (row) => !!selectedMap[`${row.approval_type}::${row.id}`];
  const isAllPageSelected = selectableRows.length > 0 && selectableRows.every(r => isRowSelected(r));

  const toggleRowSelection = (row, e) => {
    e?.stopPropagation();
    const key = `${row.approval_type}::${row.id}`;
    setSelectedMap(prev => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = row;
      }
      return next;
    });
  };

  const toggleSelectAllCurrentPage = () => {
    if (isAllPageSelected) {
      setSelectedMap(prev => {
        const next = { ...prev };
        selectableRows.forEach(r => delete next[`${r.approval_type}::${r.id}`]);
        return next;
      });
    } else {
      setSelectedMap(prev => {
        const next = { ...prev };
        selectableRows.forEach(r => { next[`${r.approval_type}::${r.id}`] = r; });
        return next;
      });
    }
  };

  const clearSelection = () => setSelectedMap({});

  const selectedItemsList = Object.values(selectedMap);
  const selectedCount = selectedItemsList.length;

  const downloadConveyancePdf = async (row, item) => {
    try {
      toast.info('Generating conveyance PDF...');
      const itemId = item.report_id || item.id || row.id;
      let url = '';
      let defaultFilename = 'conveyance_report.pdf';
      if (row.approval_type === 'local_conveyance') {
        url = `${API_URL}/ess/local-travel/reports/${itemId}/pdf`;
        defaultFilename = `Local_Conveyance_${item.report_number || itemId}.pdf`;
      } else if (row.approval_type === 'outstation_conveyance') {
        url = `${API_URL}/travel-desk/requests/${itemId}/pdf`;
        defaultFilename = `Outstation_Conveyance_${item.request_number || itemId}.pdf`;
      }
      const response = await axios.get(url, { responseType: 'blob', withCredentials: true });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = defaultFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Conveyance PDF downloaded successfully');
    } catch (err) {
      console.error('PDF download error:', err);
      let errorMsg = 'Failed to download conveyance PDF';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          errorMsg = json.detail || errorMsg;
        } catch {
          if (err.response?.statusText) errorMsg = `${err.response.statusText} (${err.response.status})`;
        }
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      toast.error(errorMsg);
    }
  };

  const resolveAttachmentUrl = (att, isDownload = false) => {
    let fileUrl = isDownload ? (att.download_url || att.url || att.data_url || '') : (att.preview_url || att.url || att.data_url || '');
    if (!fileUrl && (att.file_id || att.attachment_id || att.proof_file_id)) {
      const fId = att.file_id || att.attachment_id || att.proof_file_id;
      fileUrl = `${API_URL}/ess/compoff-requests/attachments/${fId}${isDownload ? '?download=true' : '?preview=true'}`;
    }
    if (fileUrl && fileUrl.startsWith('/') && !fileUrl.startsWith('//')) {
      const baseUrl = API_URL.replace(/\/api$/, '');
      fileUrl = `${baseUrl}${fileUrl}`;
    }
    return fileUrl;
  };

  const downloadAttachment = async (att, index) => {
    try {
      const filename = att.name || att.filename || `attachment_${index + 1}.${att.type ? att.type.split('/')[1] || 'bin' : 'pdf'}`;
      const fileUrl = resolveAttachmentUrl(att, true);
      if (!fileUrl) {
        toast.error('Attachment file not available for download');
        return;
      }
      if (fileUrl.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Downloading ${filename}`);
        return;
      }
      const res = await axios.get(fileUrl, { responseType: 'blob', withCredentials: true });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Downloading ${filename}`);
    } catch (e) {
      console.error('Failed to download attachment:', e);
      toast.error('Failed to download attachment');
    }
  };

  const viewAttachment = async (att) => {
    try {
      const fileUrl = resolveAttachmentUrl(att, false);
      if (!fileUrl) {
        toast.error('Attachment preview not available');
        return;
      }
      if (fileUrl.startsWith('data:')) {
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      const res = await axios.get(fileUrl, { responseType: 'blob', withCredentials: true });
      const contentType = res.headers['content-type'] || att.type || 'application/octet-stream';
      const blob = new Blob([res.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('Failed to preview attachment:', e);
      toast.error('Attachment preview not available');
    }
  };

  const openDetail = async (row) => {
    setDetail({ row, data: null });
    setRemarks('');
    setApprovedAmount('');
    setLocalConveyanceKm('');
    setIsAmountOverride(false);
    setLocalConveyanceOverrideAmount('');
    setLocalConveyanceOverrideReason('');
    setDetailLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/manager/approvals/${row.approval_type}/${row.id}/detail`,
        {
          params: {
            employeeId: row.employee_id,
            userId: row.user_id || row.created_by || row.applied_by,
            companyId: row.company_id,
            tenantId: row.tenant_id,
          },
          withCredentials: true,
        },
      );
      const item = res.data?.item || {};
      setDetail({ row, data: res.data });
      if (row.approval_type === 'local_conveyance') {
        const initKm = item.revised_km !== undefined && item.revised_km !== null
          ? item.revised_km
          : (item.total_km !== undefined && item.total_km !== null ? item.total_km : '');
        setLocalConveyanceKm(initKm !== '' ? String(initKm) : '');
        setIsAmountOverride(Boolean(item.is_amount_overridden));
        setLocalConveyanceOverrideAmount(
          item.override_amount !== undefined && item.override_amount !== null
            ? String(item.override_amount)
            : ''
        );
        setLocalConveyanceOverrideReason(item.override_reason || '');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load details');
    }
    setDetailLoading(false);
  };

  const act = async (row, action) => {
    if ((action === 'reject' || action === 'rework') && !remarks.trim()) {
      toast.error('Remarks are required to reject or send back');
      return;
    }
    setActing(true);
    try {
      const payload = { action, remarks };
      if (action === 'approve' && approvedAmount !== '' && AMOUNT_EDIT_TYPES.includes(row.approval_type)) {
        payload.approved_amount = parseFloat(approvedAmount);
        payload.approved_advance_amount = parseFloat(approvedAmount);
      }
      if (action === 'approve' && row.approval_type === 'local_conveyance') {
        const dItemRef = detail?.data?.item || {};
        const lcCalc = getLocalConveyanceDetails(dItemRef, localConveyanceKm, isAmountOverride, localConveyanceOverrideAmount);
        if (localConveyanceKm !== '') {
          const parsedKm = Number(localConveyanceKm);
          if (isNaN(parsedKm) || parsedKm < 0) {
            toast.error('Distance in KM must be a non-negative number');
            setActing(false);
            return;
          }
          payload.revised_km = parsedKm;
        }
        if (isAmountOverride) {
          const parsedOverride = Number(localConveyanceOverrideAmount);
          if (localConveyanceOverrideAmount === '' || isNaN(parsedOverride) || parsedOverride < 0) {
            toast.error('Override amount must be a non-negative number');
            setActing(false);
            return;
          }
          if (!localConveyanceOverrideReason.trim()) {
            toast.error('Override reason is required when overriding claim amount');
            setActing(false);
            return;
          }
          payload.override_amount = parsedOverride;
          payload.override_reason = localConveyanceOverrideReason.trim();
          payload.approved_amount = parsedOverride;
        } else {
          payload.approved_amount = lcCalc.finalAmount;
        }
      }
      const res = await axios.post(
        `${API_URL}/manager/approve/${row.approval_type}/${row.id}`,
        payload, { withCredentials: true },
      );
      toast.success(res.data?.message || 'Action recorded');
      setDetail(null);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
    setActing(false);
  };

  // Execute Universal Bulk Approval
  const submitBulkAction = async () => {
    if (selectedCount === 0) return;
    const action = bulkConfirmModal.action;
    if ((action === 'reject' || action === 'send_back' || action === 'rework') && !bulkRemarks.trim()) {
      toast.error('Remarks are required for bulk rejection or send-back');
      return;
    }

    setBulkProcessing(true);
    try {
      const payload = {
        action: action,
        remarks: bulkRemarks,
        requests: selectedItemsList.map(item => ({
          id: item.id,
          approval_type: item.approval_type
        }))
      };

      const res = await axios.post(`${API_URL}/approvals/bulk-approve`, payload, { withCredentials: true });
      
      toast.success(res.data?.message || 'Bulk action completed');
      setBulkConfirmModal({ open: false, action: 'approve' });
      setBulkRemarks('');
      setSelectedMap({});
      
      // Render results modal
      setBulkResultModal({
        open: true,
        data: res.data
      });

      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkProcessing(false);
    }
  };

  const resetFilters = () => {
    setSearchQuery('');
    setFromDate('');
    setToDate('');
    setDepartmentFilter('');
    setDesignationFilter('');
    setLevelFilter('');
    setTypeFilter('');
  };

  const ctx = detail?.data?.context || {};
  const dRow = detail?.row;
  const dItem = detail?.data?.item || {};
  const isPendingRow = dRow && (['pending', 'pending_hr', 'in_progress', 'submitted'].includes((dRow.status || '').toLowerCase()) || isActionableTab);

  return (
    <div data-testid="manager-approval-inbox" className="space-y-4">
      {/* Top Filter Panel */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3 shadow-sm">
        {/* Row 1: Status tabs + Type filter pills */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
          <div className="inline-flex bg-slate-100 rounded-lg p-1">
            {STATUS_TABS.map((t) => (
              <button key={t.key} data-testid={`inbox-status-${t.key}`}
                onClick={() => setStatusTab(t.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                  statusTab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              data-testid="toggle-filters-btn"
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border flex items-center gap-1.5 transition ${
                showAdvancedFilters || searchQuery || fromDate || toDate || departmentFilter || designationFilter || levelFilter
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Filter className="w-3.5 h-3.5" />
              <span>Filters</span>
              {(searchQuery || fromDate || toDate || departmentFilter || designationFilter || levelFilter) && (
                <span className="w-2 h-2 rounded-full bg-blue-600"></span>
              )}
              {showAdvancedFilters ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />}
            </button>

            <button
              onClick={fetchRows}
              className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 border border-slate-200 hover:bg-slate-50"
              title="Refresh Approval List"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Row 2: Type Filter Chips */}
        <div className="flex flex-wrap gap-1.5">
          {TYPE_FILTERS.map((t) => (
            <button key={t.key} data-testid={`inbox-type-${t.key || 'all'}`}
              onClick={() => setTypeFilter(t.key)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition ${
                typeFilter === t.key
                  ? 'bg-[#0052FF] text-white border-[#0052FF]'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Advanced Filters Drawer / Grid */}
        {showAdvancedFilters && (
          <div className="pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 animate-in fade-in duration-150">
            {/* Search Input */}
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-slate-500">Employee / Request</label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Code, Name, Email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* Department */}
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-slate-500">Department</label>
              <input
                type="text"
                placeholder="Department name..."
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
              />
            </div>

            {/* Designation */}
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-slate-500">Designation</label>
              <input
                type="text"
                placeholder="Designation..."
                value={designationFilter}
                onChange={(e) => setDesignationFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
              />
            </div>

            {/* From Date */}
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-slate-500">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
              />
            </div>

            {/* To Date */}
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-slate-500">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
              />
            </div>

            {/* Approval Level */}
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-slate-500">Approval Level</label>
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
              >
                <option value="">All Levels</option>
                <option value="1">Level 1 (Reporting Manager)</option>
                <option value="2">Level 2 (HR / Next Level)</option>
                <option value="3">Level 3 (Finance / Director)</option>
              </select>
            </div>

            <div className="col-span-full flex justify-end gap-2 pt-1">
              <button
                onClick={resetFilters}
                className="px-3 py-1 rounded text-xs text-slate-500 hover:text-slate-800 underline font-medium"
              >
                Reset All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sticky Bulk Selection Toolbar */}
      {selectedCount > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="bg-[#0052FF]/10 border border-[#0052FF]/30 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3 shadow-sm animate-in fade-in duration-200"
        >
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#0052FF] text-white flex items-center gap-1.5">
              <CheckSquare className="w-3.5 h-3.5" />
              {selectedCount} approval{selectedCount > 1 ? 's' : ''} selected
            </span>
            <button
              onClick={clearSelection}
              className="text-xs text-slate-600 hover:text-slate-900 underline font-medium"
            >
              Clear Selection
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              data-testid="bulk-approve-btn"
              onClick={() => { setBulkRemarks(''); setBulkConfirmModal({ open: true, action: 'approve' }); }}
              className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm flex items-center gap-1.5 transition"
            >
              <CheckCircle className="w-3.5 h-3.5" /> Bulk Approve ({selectedCount})
            </button>
            <button
              data-testid="bulk-reject-btn"
              onClick={() => { setBulkRemarks(''); setBulkConfirmModal({ open: true, action: 'reject' }); }}
              className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-red-600 hover:bg-red-700 text-white shadow-sm flex items-center gap-1.5 transition"
            >
              <XCircle className="w-3.5 h-3.5" /> Bulk Reject ({selectedCount})
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading approvals…
          </div>
        ) : fetchError ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500" data-testid="inbox-error">
            <Inbox className="w-8 h-8 mb-2 text-red-400" />
            <p className="text-sm font-semibold text-slate-700">{fetchError}</p>
            <button
              onClick={fetchRows}
              className="mt-3 px-4 py-1.5 rounded-lg text-xs font-semibold bg-slate-900 text-white hover:bg-slate-700 transition"
            >
              Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400" data-testid="inbox-empty">
            <Inbox className="w-8 h-8 mb-2" />
            <p className="text-sm">No {STATUS_TABS.find(t => t.key === statusTab)?.label.toLowerCase() || statusTab.replace('_', ' ')} requests match the criteria</p>
          </div>
        ) : (
          <table className="w-full text-left" data-testid={statusTab === 'my_actions' ? 'my-actions-table' : 'inbox-table'}>
            <thead>
              <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50/50">
                {isActionableTab && (
                  <th className="px-3 py-2.5 w-10 text-center">
                    <input
                      type="checkbox"
                      data-testid="select-all-checkbox"
                      checked={isAllPageSelected}
                      onChange={toggleSelectAllCurrentPage}
                      className="rounded border-slate-300 text-[#0052FF] focus:ring-[#0052FF] h-4 w-4 cursor-pointer"
                      title="Select / Deselect all on current view"
                    />
                  </th>
                )}
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Employee</th>
                <th className="px-4 py-2.5">Department</th>
                <th className="px-4 py-2.5">Request Date</th>
                <th className="px-4 py-2.5">Amount / Days / Period</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Pending With</th>
                <th className="px-4 py-2.5">Ageing</th>
                <th className="px-4 py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const selected = isRowSelected(row);
                return (
                  <tr key={`${row.approval_type}-${row.id}`}
                    data-testid={statusTab === 'my_actions' ? `my-action-row-${idx}` : `inbox-row-${row.approval_type}-${row.id}`}
                    className={`border-b border-slate-50 last:border-0 hover:bg-slate-50/70 cursor-pointer transition ${
                      selected ? 'bg-blue-50/50 hover:bg-blue-50/80' : ''
                    }`}
                    onClick={() => openDetail(row)}>
                    {isActionableTab && (
                      <td className="px-3 py-3 w-10 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          data-testid={`select-row-${row.approval_type}-${row.id}`}
                          checked={selected}
                          onChange={(e) => toggleRowSelection(row, e)}
                          className="rounded border-slate-300 text-[#0052FF] focus:ring-[#0052FF] h-4 w-4 cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold text-slate-800">{row.approval_label || row.request_type_label || row.approval_type}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-slate-800">{row.employee_name || '—'}</p>
                      <p className="text-[11px] text-slate-400 font-mono">{row.employee_code}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{row.department || '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {(row.request_date || row.created_at || '').slice(0, 10)}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">{summaryText(row)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ${STATUS_CHIP[row.status] || 'bg-slate-100 text-slate-600'}`}>
                        {row.display_status || row.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{row.pending_with || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs ${row.ageing_days >= 7 ? 'text-red-600 font-semibold' : row.ageing_days >= 3 ? 'text-amber-600' : 'text-slate-500'}`}>
                        <Clock className="w-3 h-3" />{row.ageing_days ?? 0}d
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={statusTab === 'my_actions' ? `my-action-view-${row.id}` : `inbox-review-${row.id}`}
                        onClick={(e) => { e.stopPropagation(); openDetail(row); }}
                        className="px-3 py-1 rounded-lg text-xs font-semibold bg-slate-900 text-white hover:bg-slate-700">
                        {isActionableTab ? 'Review' : 'Details'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Bulk Approval Confirmation Modal */}
      {bulkConfirmModal.open && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200" data-testid="bulk-confirmation-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-slate-200 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                {bulkConfirmModal.action === 'approve' ? (
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600" />
                )}
                <h3 className="text-base font-bold text-slate-900">
                  {bulkConfirmModal.action === 'approve' ? 'Confirm Bulk Approval' : 'Confirm Bulk Rejection'}
                </h3>
              </div>
              <button
                onClick={() => setBulkConfirmModal({ open: false, action: 'approve' })}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-2">
              <p className="text-xs font-semibold text-slate-700">
                You are about to {bulkConfirmModal.action === 'approve' ? 'approve' : 'reject'} {selectedCount} selected request{selectedCount > 1 ? 's' : ''}:
              </p>
              <div className="max-h-36 overflow-y-auto space-y-1.5 pr-1 text-xs">
                {selectedItemsList.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center py-1 border-b border-slate-100 last:border-0">
                    <span className="font-mono text-slate-600">{item.employee_code} ({item.employee_name})</span>
                    <span className="font-semibold text-slate-800">{item.approval_label}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 pt-1">
                Each request will be processed independently according to configured approval levels and authorization rules.
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">
                Remarks / Reason {bulkConfirmModal.action === 'reject' ? '(Required)' : '(Optional)'}
              </label>
              <textarea
                rows={3}
                data-testid="bulk-remarks-input"
                placeholder={bulkConfirmModal.action === 'reject' ? 'Enter reason for bulk rejection...' : 'Optional approval comments...'}
                value={bulkRemarks}
                onChange={(e) => setBulkRemarks(e.target.value)}
                className="w-full border border-slate-300 rounded-lg p-2.5 text-xs focus:outline-none focus:border-blue-500"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                disabled={bulkProcessing}
                onClick={() => setBulkConfirmModal({ open: false, action: 'approve' })}
                className="px-4 py-2 rounded-lg text-xs font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                disabled={bulkProcessing || (bulkConfirmModal.action === 'reject' && !bulkRemarks.trim())}
                data-testid="bulk-confirm-submit-btn"
                onClick={submitBulkAction}
                className={`px-5 py-2 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 shadow-sm transition ${
                  bulkConfirmModal.action === 'approve'
                    ? 'bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300'
                    : 'bg-red-600 hover:bg-red-700 disabled:bg-slate-300'
                }`}
              >
                {bulkProcessing ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                    Processing {selectedCount} requests...
                  </>
                ) : (
                  <>
                    Confirm Bulk {bulkConfirmModal.action === 'approve' ? 'Approve' : 'Reject'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Results Summary Modal */}
      {bulkResultModal.open && bulkResultModal.data && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200" data-testid="bulk-result-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl border border-slate-200 space-y-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">Bulk Approval Execution Report</h3>
                <p className="text-xs text-slate-500 mt-0.5">Per-record processing summary & status breakdown</p>
              </div>
              <button
                onClick={() => setBulkResultModal({ open: false, data: null })}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Counts Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 text-center">
                <p className="text-[10px] font-bold text-slate-400 uppercase">Selected</p>
                <p className="text-lg font-bold text-slate-800">{bulkResultModal.data.counts?.total_selected || 0}</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200 text-center">
                <p className="text-[10px] font-bold text-emerald-600 uppercase">Successful</p>
                <p className="text-lg font-bold text-emerald-700">{bulkResultModal.data.counts?.successful || 0}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 border border-blue-200 text-center">
                <p className="text-[10px] font-bold text-blue-600 uppercase">Next Level</p>
                <p className="text-lg font-bold text-blue-700">{bulkResultModal.data.counts?.moved_to_next_level || 0}</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-3 border border-amber-200 text-center">
                <p className="text-[10px] font-bold text-amber-600 uppercase">Skipped / Failed</p>
                <p className="text-lg font-bold text-amber-700">
                  {(bulkResultModal.data.counts?.unauthorized || 0) + (bulkResultModal.data.counts?.already_processed || 0) + (bulkResultModal.data.counts?.failed || 0)}
                </p>
              </div>
            </div>

            {/* Detailed Per-Item Result List */}
            <div className="flex-1 overflow-y-auto border border-slate-200 rounded-xl">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] border-b border-slate-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-2">Employee</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Result</th>
                    <th className="px-3 py-2">Message / Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {(bulkResultModal.data.results || []).map((res, i) => (
                    <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <span className="font-semibold text-slate-800">{res.employee_name || res.employee_code || res.id}</span>
                        {res.employee_code && <span className="text-[10px] text-slate-400 block font-mono">{res.employee_code}</span>}
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-700 capitalize">{res.approval_type?.replace(/_/g, ' ')}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                          res.status === 'success' && res.outcome === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                          res.status === 'success' && res.outcome === 'moved_to_next_level' ? 'bg-blue-100 text-blue-700' :
                          res.status === 'unauthorized' ? 'bg-red-100 text-red-700' :
                          res.status === 'already_processed' ? 'bg-slate-100 text-slate-600' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {res.outcome || res.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-600 max-w-xs truncate" title={res.message || res.reason}>
                        {res.message || res.reason || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setBulkResultModal({ open: false, data: null })}
                className="px-5 py-2 rounded-lg text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800 shadow-sm"
              >
                Close & Refresh Inbox
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Centered Approval Review Modal */}
      {detail && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5 md:p-6 bg-slate-900/60 backdrop-blur-xs overflow-y-auto"
          onClick={() => setDetail(null)}
          data-testid="approval-modal-backdrop"
        >
          <div
            data-testid="approval-detail-drawer"
            id="approval-detail-modal"
            className="w-full max-w-5xl md:w-[88vw] max-h-[92vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header — Fixed at top */}
            <div className="shrink-0 bg-white border-b border-slate-200 px-5 sm:px-6 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-sm shrink-0 border border-blue-100">
                  {(dRow.employee_name || 'E').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-bold text-slate-900 truncate">{dRow.approval_label}</h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                      STATUS_COLORS[dRow.display_status] ||
                      STATUS_COLORS[dRow.status] ||
                      STATUS_COLORS[(dRow.status || '').toLowerCase()] ||
                      STATUS_CHIP[(dRow.status || '').toLowerCase()] ||
                      'bg-slate-50 text-slate-700 border-slate-200'
                    }`}>
                      {dRow.display_status || dRow.status}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 font-medium truncate">
                    {dRow.employee_name} · <span className="font-mono">{dRow.employee_code}</span>
                    {dRow.department ? ` · ${dRow.department}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {(dRow.approval_type === 'local_conveyance' || dRow.approval_type === 'outstation_conveyance') && (
                  <button
                    type="button"
                    data-testid="drawer-download-pdf-btn"
                    onClick={() => downloadConveyancePdf(dRow, dItem)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white shadow-xs transition cursor-pointer"
                    title="Download Conveyance Claim PDF"
                  >
                    <FileDown className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Download PDF</span>
                  </button>
                )}
                <button
                  data-testid="detail-close"
                  onClick={() => setDetail(null)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition cursor-pointer"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body — Scrollable with Clear Sections */}
            <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
              {detailLoading ? (
                <div className="flex items-center justify-center py-16 text-slate-400">
                  <Loader2 className="w-6 h-6 animate-spin mr-2.5 text-blue-600" /> Loading request details…
                </div>
              ) : (
                <>
                  {/* Section 1: Employee Details & Request Overview (2-Column Grid) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Employee Details Card */}
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/80">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-blue-600" /> Employee Details
                      </p>
                      <DetailRow label="Employee Name" value={dRow.employee_name || dItem.employee_name || '—'} />
                      <DetailRow label="Employee Code" value={dRow.employee_code || dItem.employee_code || '—'} />
                      {(dItem.requested_on_behalf || dRow.requested_on_behalf) && (
                        <>
                          <DetailRow label="Beneficiary Employee" value={`${dItem.beneficiary_employee_name || dItem.beneficiary_name || dRow.beneficiary_name || dRow.employee_name || '—'} (${dItem.beneficiary_employee_code || dItem.beneficiary_code || dRow.beneficiary_code || dRow.employee_code || ''})`} />
                          <DetailRow label="Raised On-Behalf By" value={`${dItem.requester_employee_name || dItem.requester_name || dRow.requester_name || 'Requester'} (${dItem.requester_employee_code || dItem.requester_code || dRow.requester_code || ''})`} />
                        </>
                      )}
                      <DetailRow label="Department" value={dRow.department || dItem.department || '—'} />
                      {(dItem.designation || dItem.employee_designation) && (
                        <DetailRow label="Designation" value={dItem.designation || dItem.employee_designation} />
                      )}
                      {dItem.reporting_manager && (
                        <DetailRow label="Reporting Manager" value={dItem.reporting_manager} />
                      )}
                      {(dItem.company || dItem.company_name) && (
                        <DetailRow label="Company" value={dItem.company || dItem.company_name} />
                      )}
                    </div>

                    {/* Request Information Card */}
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/80">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                        <Inbox className="w-3.5 h-3.5 text-blue-600" /> Request Information
                      </p>
                      <DetailRow label="Request Type" value={dRow.approval_label || '—'} />
                      <DetailRow 
                        label="Submitted On" 
                        value={(() => {
                          const dt = dRow.request_date || dRow.created_at || dItem.submission_datetime || dItem.created_at;
                          if (!dt) return '—';
                          try {
                            const d = new Date(dt);
                            return isNaN(d.getTime()) ? dt : d.toLocaleString();
                          } catch (err) {
                            return dt;
                          }
                        })()} 
                      />
                      <DetailRow label="Status" value={dRow.display_status || dRow.status} />
                      <DetailRow label="Pending With" value={dRow.pending_with || 'Approver'} />
                      <DetailRow label="Ageing" value={`${dRow.ageing_days ?? 0} days`} />
                      {dRow.summary?.amount !== undefined && (
                        <DetailRow label="Submitted Amount" value={fmtAmt(dRow.summary.amount)} />
                      )}
                    </div>
                  </div>

                  {/* Workflow Callouts: Sent Back, Rejection Reason, Rework Notes, Reapplication Lineage */}
                  {(dItem.return_remarks || ctx.return_remarks || dRow.return_remarks) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-xs">
                      <div className="font-bold text-amber-900 flex items-center gap-1.5 mb-1">
                        <RotateCcw className="w-3.5 h-3.5 text-amber-600" />
                        <span>Sent Back for Correction</span>
                      </div>
                      <p className="text-amber-800">{dItem.return_remarks || ctx.return_remarks || dRow.return_remarks}</p>
                      {(dItem.sent_back_by || ctx.sent_back_by || dRow.sent_back_by) && (
                        <p className="text-[10px] text-amber-600 mt-1">Sent back by: {dItem.sent_back_by || ctx.sent_back_by || dRow.sent_back_by}</p>
                      )}
                    </div>
                  )}

                  {(dItem.rejection_reason || ctx.rejection_reason || dRow.rejection_reason) && (
                    <div className="bg-rose-50 border border-rose-200 rounded-xl p-3.5 text-xs">
                      <div className="font-bold text-rose-900 flex items-center gap-1.5 mb-1">
                        <XCircle className="w-3.5 h-3.5 text-rose-600" />
                        <span>Rejection Reason</span>
                      </div>
                      <p className="text-rose-800">{dItem.rejection_reason || ctx.rejection_reason || dRow.rejection_reason}</p>
                      {(dItem.rejected_by || ctx.rejected_by || dRow.rejected_by) && (
                        <p className="text-[10px] text-rose-600 mt-1">Rejected by: {dItem.rejected_by || ctx.rejected_by || dRow.rejected_by}</p>
                      )}
                    </div>
                  )}

                  {(dItem.rework_notes || ctx.rework_notes || dRow.rework_notes) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3.5 text-xs">
                      <div className="font-bold text-blue-900 flex items-center gap-1.5 mb-1">
                        <FileText className="w-3.5 h-3.5 text-blue-600" />
                        <span>Employee Rework / Resubmission Notes</span>
                      </div>
                      <p className="text-blue-800">{dItem.rework_notes || ctx.rework_notes || dRow.rework_notes}</p>
                    </div>
                  )}

                  {(dItem.is_reapplied || dItem.reapplied_from_id || ctx.is_reapplied || ctx.reapplied_from_id) && (
                    <div className="bg-purple-50 border border-purple-200 rounded-xl p-3.5 text-xs">
                      <div className="font-bold text-purple-900 flex items-center gap-1.5 mb-1">
                        <RefreshCw className="w-3.5 h-3.5 text-purple-600" />
                        <span>Reapplied Claim</span>
                      </div>
                      <p className="text-purple-800">
                        This claim was reapplied from historical rejected request: 
                        <b> {dItem.reapplied_from_report_number || dItem.reapplied_from_request_number || ctx.reapplied_from_report_number || ctx.reapplied_from_request_number || dItem.reapplied_from_id || ctx.reapplied_from_id}</b>
                      </p>
                    </div>
                  )}

                  {/* Dynamic Request Details */}
                  {getDisplayableFields(dItem).length > 0 && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-2">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">Submitted Details</p>
                      {getDisplayableFields(dItem).map(([key, val]) => {
                        const label = formatKey(key);
                        let displayVal = val;
                        
                        // 1. Attachments checking
                        const isAttachmentKey = /file|proof|attachment|bill|document|receipt|invoice/i.test(key);
                        
                        if (typeof val === 'string' && val.length === 24 && isAttachmentKey) {
                          return (
                            <div key={key} className="flex flex-col gap-1 py-1.5 border-b border-slate-100 last:border-0">
                              <span className="text-xs text-slate-500">{label}</span>
                              <a href={`${API_URL}/kyc/proofs/${val}?preview=true`} target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs text-[#0052FF] hover:underline font-semibold w-fit">
                                <Paperclip className="w-3.5 h-3.5" /> View / Download {label}
                              </a>
                            </div>
                          );
                        }
                        
                        if (Array.isArray(val)) {
                          const fileLinks = val.map((item, idx) => {
                            const fileId = typeof item === 'string' ? item : item.file_id || item.proof_file_id || item.id;
                            const fileName = item.name || item.filename || `${label} #${idx + 1}`;
                            if (fileId && typeof fileId === 'string' && fileId.length === 24) {
                              return (
                                <a key={idx} href={`${API_URL}/kyc/proofs/${fileId}?preview=true`} target="_blank" rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-[#0052FF] hover:underline font-semibold bg-blue-50/50 border border-blue-100 rounded px-2 py-1 mr-2 mt-1 w-fit">
                                  <Paperclip className="w-3.5 h-3.5" /> {fileName}
                                </a>
                              );
                            }
                            return null;
                          }).filter(Boolean);
                          
                          if (fileLinks.length > 0) {
                            return (
                              <div key={key} className="flex flex-col gap-1 py-1.5 border-b border-slate-100 last:border-0">
                                <span className="text-xs text-slate-500">{label}</span>
                                <div className="flex flex-wrap">{fileLinks}</div>
                              </div>
                            );
                          }
                        }
                        
                        // 2. Formatting dates and amounts
                        if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val)) {
                          try {
                            const d = new Date(val);
                            if (!isNaN(d.getTime())) {
                              displayVal = d.toLocaleString();
                            }
                          } catch (e) {}
                        } else if (typeof val === 'number' && /amount|rate|emi|pay|salary|cost|total|balance|budget/i.test(key)) {
                          displayVal = fmtAmt(val);
                        } else if (typeof val === 'boolean') {
                          displayVal = val ? 'Yes' : 'No';
                        }
                        
                        // 3. Long text wrapping
                        const isLongText = /remarks|reason|description|note|comment/i.test(key) || (typeof val === 'string' && val.length > 50);
                        if (isLongText) {
                          return (
                            <div key={key} className="flex flex-col gap-1 py-2 border-b border-slate-100 last:border-0">
                              <span className="text-xs text-slate-500">{label}</span>
                              <span className="text-xs text-slate-700 break-words whitespace-pre-wrap bg-white rounded border border-slate-200 p-2.5 leading-relaxed shadow-sm">
                                {String(displayVal)}
                              </span>
                            </div>
                          );
                        }
                        
                        return (
                          <DetailRow key={key} label={label} value={displayVal} />
                        );
                      })}
                    </div>
                  )}

                  {/* ADVANCE / LOAN */}
                  {dRow.approval_type === 'loan' && (
                    <div className="space-y-3" data-testid="detail-advance-balance">
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                        {(dItem.loan_type === 'salary_advance' || dItem.loan_type === 'advance' || dItem.loan_type === 'expense_advance') ? (
                          <>
                            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Advance Request Details</p>
                            <DetailRow label="Requested Amount" value={fmtAmt(dItem.amount)} />
                            <DetailRow label="Advance Type" value={(dItem.advance_type || dItem.loan_type || '').replace(/_/g, ' ')} />
                            <DetailRow label="Advance Category" value={(dItem.advance_category || dItem.category || dItem.loan_type || '').replace(/_/g, ' ')} />
                            <DetailRow label="Purpose" value={dItem.purpose || dItem.reason || '—'} />
                            <DetailRow label="Required Date" value={dItem.required_date || dItem.start_month || '—'} />
                            <DetailRow label="Recovery Method" value={dItem.recovery_method || '—'} />
                            <DetailRow label="Recovery Start Month" value={dItem.recovery_start_month || dItem.start_month || '—'} />
                            <DetailRow label="Number of Installments" value={dItem.installment_count || dItem.tenure_months || '—'} />
                            <DetailRow label="Outstanding Advance Balance" value={fmtAmt(dItem.outstanding_advance || ctx.advance_balance?.net_balance)} />
                            <DetailRow label="Previous Pending Advances" value={ctx.previous_pending_advances ?? 0} />
                            <DetailRow label="Employee Remarks" value={dItem.remarks || dItem.reason || '—'} />
                            {dItem.supporting_document_url && (
                              <DetailRow label="Supporting Documents" value={<a href={dItem.supporting_document_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">View Attachment</a>} />
                            )}
                            {dItem.attachments && Array.isArray(dItem.attachments) && dItem.attachments.length > 0 && (
                              <div className="mt-2">
                                <p className="text-[11px] text-slate-500 font-semibold mb-1">Attachments:</p>
                                {dItem.attachments.map((att, idx) => (
                                  <a key={idx} href={att.url || att} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-[11px] block">{att.name || 'Attachment'}</a>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Loan Request Details</p>
                            <DetailRow label="Loan Amount" value={fmtAmt(dItem.loan_amount || dItem.amount)} />
                            <DetailRow label="Loan Type" value={(dItem.loan_type || '').replace(/_/g, ' ')} />
                            <DetailRow label="Loan Purpose" value={dItem.loan_purpose || dItem.reason || '—'} />
                            <DetailRow label="Repayment Months" value={dItem.repayment_months ? `${dItem.repayment_months} months` : (dItem.tenure_months ? `${dItem.tenure_months} months` : '—')} />
                            <DetailRow label="EMI" value={fmtAmt(dItem.emi || dItem.emi_amount)} />
                            <DetailRow label="Start Month" value={dItem.start_month || '—'} />
                            <DetailRow label="Existing Loan Balance" value={fmtAmt(ctx.advance_balance?.net_balance || dItem.existing_loan_balance)} />
                            <DetailRow label="Remarks" value={dItem.remarks || dItem.reason || '—'} />
                            {dItem.supporting_document_url && (
                              <DetailRow label="Attachment" value={<a href={dItem.supporting_document_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">View Attachment</a>} />
                            )}
                            {dItem.attachments && Array.isArray(dItem.attachments) && dItem.attachments.length > 0 && (
                              <div className="mt-2">
                                <p className="text-[11px] text-slate-500 font-semibold mb-1">Attachments:</p>
                                {dItem.attachments.map((att, idx) => (
                                  <a key={idx} href={att.url || att} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-[11px] block">{att.name || 'Attachment'}</a>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* CLAIM SHEET — line details + attachments + advance ref */}
                  {dRow.approval_type === 'claim_sheet' && (
                    <div className="space-y-3" data-testid="detail-claim-lines">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Claim Lines</p>
                      {(ctx.lines || []).map((ln, i) => (
                        <div key={i} className="rounded-lg border border-slate-200 p-3 bg-white space-y-1.5 shadow-sm">
                          <div className="flex justify-between items-start border-b border-slate-100 pb-1.5 mb-1.5">
                            <div>
                              <p className="text-xs font-semibold text-slate-800">{ln.head_code}{ln.subcategory ? ` · ${ln.subcategory}` : ''}</p>
                              <p className="text-[10px] text-slate-500">Expense Date: {ln.bill_date}</p>
                            </div>
                            <span className="text-xs font-bold text-slate-900">{fmtAmt(ln.claim_amount)}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-600">
                            {ln.bill_number && <div><span className="text-slate-400">Bill Number:</span> {ln.bill_number}</div>}
                            {ln.vendor && <div><span className="text-slate-400">Vendor:</span> {ln.vendor}</div>}
                            {ln.gst && <div><span className="text-slate-400">GST:</span> {ln.gst}</div>}
                            {ln.cost_center && <div><span className="text-slate-400">Cost Center:</span> {ln.cost_center}</div>}
                            {ln.project && <div><span className="text-slate-400">Project:</span> {ln.project}</div>}
                            {ln.payment_mode && <div><span className="text-slate-400">Payment Mode:</span> {ln.payment_mode}</div>}
                          </div>
                          {ln.description && (
                            <p className="text-[11px] text-slate-500 bg-slate-50 p-1.5 rounded mt-1.5">
                              <span className="text-slate-400 font-semibold">Description:</span> {ln.description}
                            </p>
                          )}
                          {ln.proof_file_id && (
                            <div className="pt-1.5 border-t border-slate-100 mt-1.5">
                              <a href={`${API_URL}/kyc/proofs/${ln.proof_file_id}?preview=true`} target="_blank" rel="noreferrer"
                                data-testid={`claim-attachment-${i}`}
                                className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
                                <Paperclip className="w-3 h-3" /> View Bill Attachment
                              </a>
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                        <DetailRow label="Trip" value={[dItem.trip_from, dItem.trip_to].filter(Boolean).join(' → ') || '—'} />
                        <DetailRow label="Travel Dates" value={[dItem.trip_start_date, dItem.trip_end_date].filter(Boolean).join(' → ') || '—'} />
                        <DetailRow label="Total Claim" value={fmtAmt(dItem.total_amount)} />
                        {ctx.advance_reference && (
                          <DetailRow label="Advance Reference"
                            value={`${ctx.advance_reference.loan_number || ''} — ${fmtAmt(ctx.advance_reference.principal_amount)}`} />
                        )}
                      </div>
                    </div>
                  )}

                  {/* REIMBURSEMENT — single claim */}
                  {dRow.approval_type === 'reimbursement' && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-1">
                      <DetailRow label="Category" value={dItem.category} />
                      <DetailRow label="Amount" value={fmtAmt(dItem.amount)} />
                      <DetailRow label="Bill Date" value={dItem.bill_date} />
                      {dItem.bill_number && <DetailRow label="Bill Number" value={dItem.bill_number} />}
                      {dItem.vendor && <DetailRow label="Vendor" value={dItem.vendor} />}
                      {dItem.gst && <DetailRow label="GST" value={dItem.gst} />}
                      {dItem.cost_center && <DetailRow label="Cost Center" value={dItem.cost_center} />}
                      {dItem.project && <DetailRow label="Project" value={dItem.project} />}
                      {dItem.payment_mode && <DetailRow label="Payment Mode" value={dItem.payment_mode} />}
                      {dItem.description && <DetailRow label="Description" value={dItem.description} />}
                      {dItem.proof_file_id && (
                        <div className="pt-2">
                          <a href={`${API_URL}/kyc/proofs/${dItem.proof_file_id}?preview=true`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
                            <Paperclip className="w-3 h-3" /> View Attachment
                          </a>
                        </div>
                      )}
                      {Array.isArray(dItem.attachments) && dItem.attachments.length > 0 && (
                        <div className="pt-2 space-y-1">
                          <p className="text-[10px] font-semibold text-slate-500 uppercase">Bill Attachments</p>
                          {dItem.attachments.map((att, idx) => (
                            <a
                              key={att.id || idx}
                              href={`${API_URL}/ess/reimbursements/attachments/${att.id}/download?preview=true`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline block"
                            >
                              <Paperclip className="w-3 h-3" /> {att.filename || `Attachment ${idx + 1}`}
                            </a>
                          ))}
                        </div>
                      )}
                      {Array.isArray(dItem.documents) && dItem.documents.length > 0 && (!dItem.attachments || dItem.attachments.length === 0) && (
                        <div className="pt-2 space-y-1">
                          <p className="text-[10px] font-semibold text-slate-500 uppercase">Bill Attachments</p>
                          {dItem.documents.map((doc, idx) => (
                            <a
                              key={doc.attachment_id || idx}
                              href={`${API_URL}/ess/reimbursements/attachments/${doc.attachment_id || doc.id}/download?preview=true`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline block"
                            >
                              <Paperclip className="w-3 h-3" /> {doc.filename || `Attachment ${idx + 1}`}
                            </a>
                          ))}
                        </div>
                      )}
                      {dItem.policy_check?.policy_note && (
                        <p className="text-[11px] text-amber-700 mt-2 bg-amber-50 p-2 rounded border border-amber-100">{dItem.policy_check.policy_note}</p>
                      )}
                    </div>
                  )}

                  {/* REGULARIZATION — requested vs existing attendance */}
                  {dRow.approval_type === 'regularization' && (
                    <div className="space-y-3" data-testid="detail-regularization">
                      <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-blue-700 mb-2">Requested Change</p>
                        <DetailRow label="Attendance Date" value={dItem.attendance_date} />
                        <DetailRow label="Type" value={(dItem.request_type || '').replace(/_/g, ' ')} />
                        <DetailRow label="Check-In" value={dItem.proposed_in_time || '—'} />
                        <DetailRow label="Check-Out" value={dItem.proposed_out_time || '—'} />
                        <DetailRow label="Shift" value={dItem.shift || dItem.shift_name || '—'} />
                        <DetailRow label="Alternate Status" value={dItem.alternate_status || '—'} />
                        <DetailRow label="Reason" value={dItem.reason || '—'} />
                        <DetailRow label="Employee Remarks" value={dItem.employee_remarks || dItem.reason || '—'} />
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Existing Attendance</p>
                        <DetailRow label="Current Status" value={ctx.existing_attendance?.attendance_status || ctx.existing_attendance?.status || 'No record'} />
                        <DetailRow label="In Time" value={ctx.existing_attendance?.in_time || '—'} />
                        <DetailRow label="Out Time" value={ctx.existing_attendance?.out_time || '—'} />
                      </div>
                    </div>
                  )}

                  {/* LEAVE — balance context */}
                  {dRow.approval_type === 'leave' && (
                    <div className="space-y-3" data-testid="detail-leave">
                      {/* Leave Request Details */}
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-1">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">Leave Request</p>
                        <DetailRow label="Leave Type" value={dItem.leave_type_name || dItem.leave_type || ctx.leave_balance_preview?.leave_type_name || '—'} />
                        <DetailRow label="From Date" value={dItem.from_date || '—'} />
                        <DetailRow label="To Date" value={dItem.to_date || '—'} />
                        <DetailRow 
                          label="Duration / Days" 
                          value={
                            dItem.is_permission || dItem.leave_type_code === 'ONE_HOUR_PERMISSION'
                              ? (dItem.duration_display || formatPermissionDuration(dItem.duration_minutes || 60))
                              : (dItem.is_partial_day || dItem.half_day
                                  ? `0.5 Day (${(dItem.half_day_period || dItem.half_day_session) === 'first_half' ? 'First Half' : 'Second Half'})`
                                  : (dItem.total_days !== undefined && dItem.total_days !== null ? `${dItem.total_days} Day(s)` : (dItem.days ? `${dItem.days} Day(s)` : '—'))
                                )
                          } 
                        />
                        <DetailRow label="Reason" value={dItem.reason || '—'} />
                        {(dItem.supporting_document_url || dItem.medical_certificate_url) && (
                          <div className="pt-2 border-t border-slate-200 mt-2">
                            {dItem.supporting_document_url && (
                              <a href={dItem.supporting_document_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline mr-4">
                                <Paperclip className="w-3 h-3" /> Supporting Doc
                              </a>
                            )}
                            {dItem.medical_certificate_url && (
                              <a href={dItem.medical_certificate_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
                                <Paperclip className="w-3 h-3" /> Medical Certificate
                              </a>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Employee Balance Summary — Before/After Preview */}
                      {ctx.leave_balance_preview && (
                        <div
                          data-testid="leave-balance-preview"
                          className={`rounded-xl border p-4 space-y-3 ${
                            ctx.leave_balance_preview.insufficient_balance
                              ? 'bg-red-50 border-red-200'
                              : ctx.leave_balance_preview.already_deducted
                                ? 'bg-emerald-50 border-emerald-200'
                                : 'bg-blue-50/60 border-blue-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                              <Wallet className="w-3.5 h-3.5" /> Employee Balance Summary
                            </p>
                            {ctx.leave_balance_preview.already_deducted && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-600 text-white">
                                Deducted
                              </span>
                            )}
                          </div>

                          {/* Warning banner for insufficient balance */}
                          {ctx.leave_balance_preview.insufficient_balance && ctx.leave_balance_preview.warning && (
                            <div className="flex items-start gap-2 bg-red-100 border border-red-200 rounded-lg p-2.5">
                              <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                              <p className="text-xs text-red-700 font-semibold">{ctx.leave_balance_preview.warning}</p>
                            </div>
                          )}

                          {/* Balance grid */}
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="bg-white rounded-lg border border-slate-200 p-2.5">
                              <p className="text-[10px] text-slate-500 font-semibold mb-1">Available Now</p>
                              <p className={`text-lg font-bold ${ctx.leave_balance_preview.insufficient_balance ? 'text-red-600' : 'text-slate-900'}`}>
                                {ctx.leave_balance_preview.balance_before_approval ?? '—'}
                              </p>
                              <p className="text-[10px] text-slate-400">days</p>
                            </div>
                            <div className="bg-white rounded-lg border border-slate-200 p-2.5">
                              <p className="text-[10px] text-slate-500 font-semibold mb-1">Requested</p>
                              <p className="text-lg font-bold text-amber-600">
                                {ctx.leave_balance_preview.requested_days ?? '—'}
                              </p>
                              <p className="text-[10px] text-slate-400">days</p>
                            </div>
                            <div className={`rounded-lg border p-2.5 ${
                              ctx.leave_balance_preview.insufficient_balance
                                ? 'bg-red-100 border-red-300'
                                : 'bg-emerald-50 border-emerald-200'
                            }`}>
                              <p className="text-[10px] text-slate-500 font-semibold mb-1">After Approval</p>
                              <p className={`text-lg font-bold ${ctx.leave_balance_preview.insufficient_balance ? 'text-red-700' : 'text-emerald-700'}`}>
                                {ctx.leave_balance_preview.expected_balance_after_approval ?? '—'}
                              </p>
                              <p className="text-[10px] text-slate-400">days</p>
                            </div>
                          </div>

                          {/* Detailed rows */}
                          <div className="space-y-1 bg-white rounded-lg border border-slate-100 p-3">
                            <DetailRow label="Leave Type" value={ctx.leave_balance_preview.leave_type_name || ctx.leave_balance_preview.leave_type_code} />
                            <DetailRow label="Opening Balance" value={`${ctx.leave_balance_preview.opening_balance ?? ctx.leave_balance_preview.total_entitled ?? 0} days`} />
                            {(ctx.leave_balance_preview.accrued ?? 0) > 0 && (
                              <DetailRow label="Accrued Credits" value={`${ctx.leave_balance_preview.accrued} days`} />
                            )}
                            <DetailRow label="Already Used" value={`${ctx.leave_balance_preview.already_availed ?? 0} days`} />
                            {(ctx.leave_balance_preview.pending_approval ?? 0) > 0 && (
                              <DetailRow label="Pending Approval" value={`${ctx.leave_balance_preview.pending_approval} days`} />
                            )}
                            <DetailRow label="Currently Available" value={`${ctx.leave_balance_preview.balance_before_approval ?? 0} days`} />
                            <DetailRow label="This Request" value={`${ctx.leave_balance_preview.requested_days ?? 0} day(s)`} />
                            <DetailRow
                              label="Balance After Approval"
                              value={`${ctx.leave_balance_preview.expected_balance_after_approval ?? 0} days`}
                            />
                            <DetailRow label="Leave Year" value={ctx.leave_balance_preview.leave_year || '—'} />
                          </div>

                          <p className={`text-[11px] font-semibold flex items-center gap-1.5 ${
                            ctx.leave_balance_preview.already_deducted
                              ? 'text-emerald-700'
                              : 'text-slate-500'
                          }`}>
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            {ctx.leave_balance_preview.deduction_stage_note}
                          </p>
                        </div>
                      )}

                      {/* All Leave Balances */}
                      {(ctx.leave_balances || []).length > 0 && (
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">All Leave Balances</p>
                          {(ctx.leave_balances || []).map((lb, i) => (
                            <DetailRow
                              key={i}
                              label={lb.leave_type_code || lb.leave_type_name || lb.leave_type || 'Balance'}
                              value={`${lb.closing_balance ?? lb.available_balance ?? lb.balance ?? 0} days`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* PERMISSION */}
                  {dRow.approval_type === 'permission' && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-1" data-testid="detail-permission">
                      <DetailRow label="Permission Date" value={dItem.permission_date || dRow.request_date || '—'} />
                      <DetailRow label="Time Range" value={dItem.start_time && dItem.end_time ? `${dItem.start_time} - ${dItem.end_time}` : '—'} />
                      <DetailRow label="Duration" value={dItem.duration_display || formatPermissionDuration(dItem.duration_minutes || (dItem.duration_hours ? dItem.duration_hours * 60 : 60))} />
                      <DetailRow label="Reason / Purpose" value={dItem.reason || '—'} />
                    </div>
                  )}

                  {/* OT */}
                  {dRow.approval_type === 'ot' && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-1" data-testid="detail-ot">
                      <DetailRow label="OT Date" value={dItem.ot_date || dItem.date || '—'} />
                      <DetailRow label="Hours" value={dItem.hours ?? '—'} />
                      {dItem.shift && <DetailRow label="Shift" value={dItem.shift} />}
                      <DetailRow label="Reason" value={dItem.reason || '—'} />
                    </div>
                  )}

                  {/* SHIFT CHANGE */}
                  {dRow.approval_type === 'shift' && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-1" data-testid="detail-shift">
                      <DetailRow label="Current Shift" value={dItem.current_shift || '—'} />
                      <DetailRow label="Requested Shift" value={dItem.requested_shift || '—'} />
                      <DetailRow label="Effective Date" value={dItem.effective_date || dItem.requested_date || dItem.from_date || '—'} />
                      <DetailRow label="Reason" value={dItem.reason || '—'} />
                    </div>
                  )}

                  {/* COMP-OFF */}
                  {(dRow.approval_type === 'compoff' || dRow.approval_type === 'comp_off') && (() => {
                    const attsList = [];
                    if (Array.isArray(dItem.attachments)) {
                      dItem.attachments.forEach(a => {
                        if (a) attsList.push(typeof a === 'string' ? { name: 'Attachment', url: a } : a);
                      });
                    }
                    if (attsList.length === 0 && (dItem.file_id || dItem.proof_file_id || dItem.attachment_id || dItem.attachment_name || dItem.attachment_url || dItem.data_url)) {
                      attsList.push({
                        name: dItem.attachment_name || 'Comp-Off Document',
                        filename: dItem.attachment_name || 'Comp-Off Document',
                        file_id: dItem.file_id || dItem.proof_file_id || dItem.attachment_id,
                        attachment_id: dItem.file_id || dItem.proof_file_id || dItem.attachment_id,
                        size: dItem.attachment_size,
                        size_formatted: dItem.attachment_size ? `${Math.round(dItem.attachment_size / 1024)} KB` : '',
                        type: dItem.attachment_type || 'Document',
                        url: dItem.attachment_url,
                        data_url: dItem.data_url,
                      });
                    }

                    return (
                      <div className="space-y-3" data-testid="detail-compoff">
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-1">
                          <DetailRow label="Worked Date" value={dItem.worked_date || dItem.work_date || '—'} />
                          <DetailRow label="Hours Worked" value={dItem.hours_worked !== undefined && dItem.hours_worked !== null ? `${dItem.hours_worked} hrs` : (dItem.hours ? `${dItem.hours} hrs` : '—')} />
                          <DetailRow label="Target Leave Date" value={dItem.comp_off_date || dItem.target_leave_date || dItem.eligible_date || '—'} />
                          <DetailRow label="Earned Credit" value={dItem.earned_days !== undefined ? `${dItem.earned_days} days` : (dItem.days ? `${dItem.days} days` : '—')} />
                          {(dItem.available_compoff_balance !== undefined || ctx.available_compoff_balance !== undefined) && (
                            <DetailRow label="Current Available Balance" value={`${dItem.available_compoff_balance ?? ctx.available_compoff_balance} days`} />
                          )}
                          <DetailRow label="Reason / Work Details" value={dItem.reason || '—'} />
                        </div>

                        {/* Dedicated Comp-Off Attachments Card */}
                        <div className="bg-white rounded-xl p-4 border border-slate-200 space-y-2 shadow-xs" data-testid="compoff-attachments-card">
                          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-700 flex items-center gap-1.5">
                              <Paperclip className="w-3.5 h-3.5 text-blue-600" /> Supporting Attachment
                              {attsList.length > 0 && (
                                <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] bg-blue-100 text-blue-700 font-semibold" data-testid="compoff-attachment-count">
                                  {attsList.length}
                                </span>
                              )}
                            </p>
                          </div>
                          {attsList.length === 0 ? (
                            <p className="text-xs text-slate-400 italic py-1" data-testid="no-attachment-message">
                              No attachment available
                            </p>
                          ) : (
                            <div className="divide-y divide-slate-100">
                              {attsList.map((att, idx) => {
                                const attName = att.name || att.filename || `Attachment #${idx + 1}`;
                                const attType = (att.type || attName.split('.').pop() || 'FILE').toUpperCase();
                                const attSize = typeof att.size === 'number'
                                  ? (att.size >= 1024 * 1024 ? `${(att.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(att.size / 1024)} KB`)
                                  : (att.size_formatted || att.size || '');
                                return (
                                  <div key={idx} className="flex items-center justify-between py-2 gap-2" data-testid={`compoff-attachment-item-${idx}`}>
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className="p-1.5 rounded-lg bg-blue-50 text-blue-600 shrink-0">
                                        <FileText className="w-4 h-4" />
                                      </span>
                                      <div className="min-w-0">
                                        <p className="text-xs font-semibold text-slate-800 truncate" title={attName}>{attName}</p>
                                        <div className="flex items-center gap-2 text-[10px] text-slate-400">
                                          <span className="font-mono bg-slate-100 px-1 rounded">{attType}</span>
                                          {attSize && <span>{attSize}</span>}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      <button
                                        type="button"
                                        data-testid={`view-compoff-attachment-${idx}`}
                                        onClick={() => viewAttachment(att)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition"
                                      >
                                        <Eye className="w-3 h-3" /> View
                                      </button>
                                      <button
                                        type="button"
                                        data-testid={`download-compoff-attachment-${idx}`}
                                        onClick={() => downloadAttachment(att, idx)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded transition"
                                      >
                                        <Download className="w-3 h-3" /> Download
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* LOCAL CONVEYANCE — with interactive KM editing and amount override */}
                  {dRow.approval_type === 'local_conveyance' && (() => {
                    const lc = getLocalConveyanceDetails(dItem, localConveyanceKm, isAmountOverride, localConveyanceOverrideAmount);
                    return (
                      <div className="space-y-4" data-testid="detail-local-conveyance">
                        {/* Travel Details & Route */}
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/80 space-y-2">
                          <div className="flex items-center justify-between border-b border-slate-200/60 pb-2">
                            <p className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                              <MapPin className="w-4 h-4 text-blue-600" /> Travel Details & Route
                            </p>
                            <span className="font-mono text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-200">
                              {dItem.report_number || dItem.report_id || dRow.request_number || '—'}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                            <DetailRow label="Report Date" value={dItem.report_date || '—'} />
                            <DetailRow label="Travel Purpose" value={dItem.travel_purpose || dItem.purpose || '—'} />
                            <DetailRow label="Route" value={`${dItem.from_location || '—'} → ${dItem.to_location || '—'}`} />
                            <DetailRow label="Travel Mode" value={dItem.travel_mode || '—'} />
                            {dItem.vehicle_number && <DetailRow label="Vehicle Number" value={dItem.vehicle_number} />}
                          </div>
                          {dItem.stops && dItem.stops.length > 0 && (
                            <div className="pt-2 border-t border-slate-200/60">
                              <span className="text-xs text-slate-500 block mb-1">Stops / Intermediate Points:</span>
                              <div className="flex flex-wrap gap-1">
                                {dItem.stops.map((stop, sIdx) => (
                                  <span key={sIdx} className="px-2 py-0.5 rounded text-[11px] bg-slate-200 text-slate-800 font-medium">
                                    {typeof stop === 'string' ? stop : stop.name || stop.location || `Stop #${sIdx + 1}`}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Interactive KM & Calculation Editor Card */}
                        <div className="bg-gradient-to-br from-blue-50/60 to-indigo-50/40 rounded-xl p-4 border border-blue-200 space-y-3">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-bold text-blue-900 flex items-center gap-1.5">
                              <Gauge className="w-4 h-4 text-blue-600" /> Distance, Policy Rate & Calculation
                            </p>
                            {lc.isKmChanged && (
                              <span className="text-[11px] font-semibold bg-amber-100 text-amber-800 px-2.5 py-0.5 rounded-full border border-amber-300">
                                KM Revised: {lc.origKm} → {lc.currentKm} KM
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {/* Reported Distance */}
                            <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-xs">
                              <span className="text-[11px] text-slate-500 font-medium block">Reported Distance</span>
                              <p className="text-base font-bold text-slate-800 mt-1">{lc.origKm} KM</p>
                              <span className="text-[10px] text-slate-400">
                                {dItem.start_odometer && dItem.end_odometer ? `Odo: ${dItem.start_odometer} - ${dItem.end_odometer}` : 'Reported by employee'}
                              </span>
                            </div>

                            {/* Editable Approved KM Input */}
                            <div className="bg-white rounded-lg p-3 border border-blue-300 shadow-xs ring-2 ring-blue-500/10">
                              <div className="flex items-center justify-between">
                                <label htmlFor="local-conveyance-km-input" className="text-[11px] text-blue-900 font-bold flex items-center gap-1">
                                  <Edit3 className="w-3 h-3 text-blue-600" /> Approved KM {isPendingRow ? '(Editable)' : ''}
                                </label>
                                {isPendingRow && lc.isKmChanged && (
                                  <button
                                    type="button"
                                    onClick={() => setLocalConveyanceKm(String(lc.origKm))}
                                    className="text-[10px] text-blue-600 hover:underline font-semibold"
                                  >
                                    Reset
                                  </button>
                                )}
                              </div>
                              <div className="mt-1 flex items-center gap-1.5">
                                <input
                                  id="local-conveyance-km-input"
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  data-testid="local-conveyance-km-input"
                                  disabled={!isPendingRow}
                                  value={localConveyanceKm}
                                  onChange={(e) => setLocalConveyanceKm(e.target.value)}
                                  className="w-full text-base font-bold text-slate-900 border border-slate-300 rounded px-2.5 py-1 focus:outline-none focus:border-blue-600 disabled:bg-slate-50"
                                  placeholder={String(lc.origKm)}
                                />
                                <span className="text-xs font-semibold text-slate-500">KM</span>
                              </div>
                              {lc.isKmInvalid && (
                                <p className="text-[10px] text-red-600 mt-0.5">Please enter a non-negative distance.</p>
                              )}
                            </div>

                            {/* Policy Fare Rate */}
                            <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-xs">
                              <span className="text-[11px] text-slate-500 font-medium block">Policy Rate</span>
                              <p className="text-base font-bold text-slate-800 mt-1">
                                {lc.rate > 0 ? `₹${lc.rate.toFixed(2)}` : '—'} <span className="text-xs font-normal text-slate-500">/ KM</span>
                              </p>
                              <span className="text-[10px] text-slate-400">
                                {dItem.policy_snapshot?.policy_name ? `Policy: ${dItem.policy_snapshot.policy_name}` : 'Applicable Policy Rate'}
                              </span>
                            </div>
                          </div>

                          {/* Instant Conveyance Calculation Banner */}
                          <div className="bg-white/90 rounded-lg p-3 border border-blue-100 flex flex-wrap items-center justify-between gap-2 text-xs">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-slate-600 font-medium">Conveyance Fare:</span>
                              <span className="font-mono bg-blue-50 px-2.5 py-1 rounded text-blue-900 font-semibold border border-blue-200">
                                {lc.currentKm} KM × ₹{lc.rate.toFixed(2)}/KM = ₹{lc.conveyanceFare.toFixed(2)}
                              </span>
                            </div>
                            {lc.otherExpenses > 0 && (
                              <div className="text-slate-500 text-[11px]">
                                + Other Expenses: ₹{lc.otherExpenses.toFixed(2)}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Itemized Expenses Breakdown */}
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/80 space-y-1 text-xs">
                          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                            <IndianRupee className="w-3.5 h-3.5 text-blue-600" /> Itemized Expense Breakdown
                          </p>
                          <DetailRow label="Conveyance Amount (Fare)" value={`₹${lc.conveyanceFare.toFixed(2)}`} />
                          {lc.food > 0 && <DetailRow label="Daily Allowance / Food" value={fmtAmt(lc.food)} />}
                          {lc.parkingToll > 0 && <DetailRow label="Parking & Toll Charges" value={fmtAmt(lc.parkingToll)} />}
                          {lc.other > 0 && <DetailRow label="Other Incidental Expenses" value={fmtAmt(lc.other)} />}
                          <div className="pt-2 border-t border-slate-200 mt-2 space-y-1 font-semibold">
                            <DetailRow label="Submitted Claim Amount" value={fmtAmt(dItem.total_amount || dItem.claim_amount || dRow.summary?.amount)} />
                            <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                              <span className="text-xs text-slate-600">Policy-Calculated Total Amount</span>
                              <span className="text-xs font-bold text-slate-900" data-testid="local-conveyance-calculated-amount">
                                ₹{lc.policyCalculatedTotal.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Override Amount Control Card */}
                        <div className="bg-amber-50/70 rounded-xl p-4 border border-amber-200 space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                data-testid="local-conveyance-override-toggle"
                                disabled={!isPendingRow}
                                checked={isAmountOverride}
                                onChange={(e) => {
                                  setIsAmountOverride(e.target.checked);
                                  if (e.target.checked && !localConveyanceOverrideAmount) {
                                    setLocalConveyanceOverrideAmount(String(lc.policyCalculatedTotal));
                                  }
                                }}
                                className="w-4 h-4 rounded text-amber-600 focus:ring-amber-500 border-slate-300"
                              />
                              <span className="text-xs font-bold text-amber-900">
                                Override Claim Amount (Explicit Manager Authorization)
                              </span>
                            </label>
                            {isAmountOverride && (
                              <span className="text-[10px] font-bold uppercase bg-amber-200 text-amber-900 px-2 py-0.5 rounded border border-amber-300">
                                Override Enabled
                              </span>
                            )}
                          </div>

                          {isAmountOverride && (
                            <div className="space-y-3 pt-2 border-t border-amber-200/80">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label htmlFor="local-conveyance-override-amount-input" className="block text-[11px] font-bold text-amber-900 mb-1">
                                    Authorized Override Amount (₹) *
                                  </label>
                                  <div className="relative">
                                    <IndianRupee className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
                                    <input
                                      id="local-conveyance-override-amount-input"
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      data-testid="local-conveyance-override-amount-input"
                                      disabled={!isPendingRow}
                                      value={localConveyanceOverrideAmount}
                                      onChange={(e) => setLocalConveyanceOverrideAmount(e.target.value)}
                                      placeholder={`e.g. ${lc.policyCalculatedTotal}`}
                                      className="w-full border-2 border-amber-300 rounded-lg pl-8 pr-3 py-1.5 text-sm font-bold text-slate-900 focus:outline-none focus:border-amber-600 bg-white"
                                    />
                                  </div>
                                </div>

                                <div className="bg-white/90 p-2.5 rounded-lg border border-amber-200 flex flex-col justify-center">
                                  <span className="text-[10px] text-slate-500 font-medium">Comparison:</span>
                                  <p className="text-xs text-slate-700">
                                    Policy Amount: <span className="font-semibold">₹{lc.policyCalculatedTotal.toFixed(2)}</span>
                                  </p>
                                  <p className="text-xs font-bold text-amber-800">
                                    Authorized Override: ₹{Number(localConveyanceOverrideAmount || 0).toFixed(2)}
                                  </p>
                                </div>
                              </div>

                              <div>
                                <label htmlFor="local-conveyance-override-reason-input" className="block text-[11px] font-bold text-amber-900 mb-1">
                                  Override Reason (Mandatory) *
                                </label>
                                <textarea
                                  id="local-conveyance-override-reason-input"
                                  rows={2}
                                  data-testid="local-conveyance-override-reason-input"
                                  disabled={!isPendingRow}
                                  value={localConveyanceOverrideReason}
                                  onChange={(e) => setLocalConveyanceOverrideReason(e.target.value)}
                                  placeholder="Provide explicit business justification or policy exception reason for overriding amount..."
                                  className="w-full border border-amber-300 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-amber-600 bg-white"
                                />
                              </div>
                            </div>
                          )}

                          {/* Final Approved Amount Highlight Card */}
                          <div className="bg-white rounded-lg p-3 border border-slate-200 flex items-center justify-between">
                            <div>
                              <span className="text-xs font-bold text-slate-800">Final Amount to Approve:</span>
                              <p className="text-[11px] text-slate-500">
                                {isAmountOverride
                                  ? `Explicit authorized override (${localConveyanceOverrideReason || 'Reason pending'})`
                                  : (lc.isKmChanged ? `Recalculated from edited distance (${lc.currentKm} KM @ ₹${lc.rate.toFixed(2)}/KM)` : 'Standard policy calculation')}
                              </p>
                            </div>
                            <span className="text-lg font-bold text-emerald-700" data-testid="local-conveyance-final-amount">
                              ₹{lc.finalAmount.toFixed(2)}
                            </span>
                          </div>
                        </div>

                        {/* Dedicated Attachments Card */}
                        <div className="bg-white rounded-xl p-4 border border-slate-200 space-y-2 shadow-xs" data-testid="conveyance-attachments-card">
                          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-700 flex items-center gap-1.5">
                              <Paperclip className="w-3.5 h-3.5 text-blue-600" /> Supporting Attachments
                              <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] bg-blue-100 text-blue-700 font-semibold">
                                {(dItem.attachments || []).length}
                              </span>
                            </p>
                          </div>
                          {(!dItem.attachments || dItem.attachments.length === 0) ? (
                            <p className="text-xs text-slate-400 italic py-1">No supporting attachments uploaded</p>
                          ) : (
                            <div className="divide-y divide-slate-100">
                              {dItem.attachments.map((att, idx) => {
                                const attName = att.name || `Attachment #${idx + 1}`;
                                const attType = (att.type || att.name?.split('.').pop() || 'FILE').toUpperCase();
                                const attSize = att.size ? `${Math.round(att.size / 1024)} KB` : '';
                                return (
                                  <div key={idx} className="flex items-center justify-between py-2 gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className="p-1.5 rounded-lg bg-blue-50 text-blue-600 shrink-0">
                                        <FileText className="w-4 h-4" />
                                      </span>
                                      <div className="min-w-0">
                                        <p className="text-xs font-semibold text-slate-800 truncate" title={attName}>{attName}</p>
                                        <div className="flex items-center gap-2 text-[10px] text-slate-400">
                                          <span className="font-mono bg-slate-100 px-1 rounded">{attType}</span>
                                          {attSize && <span>{attSize}</span>}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      <button
                                        type="button"
                                        data-testid={`view-attachment-${idx}`}
                                        onClick={() => viewAttachment(att)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition cursor-pointer"
                                      >
                                        <Eye className="w-3 h-3" /> View
                                      </button>
                                      <button
                                        type="button"
                                        data-testid={`download-attachment-${idx}`}
                                        onClick={() => downloadAttachment(att, idx)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded transition cursor-pointer"
                                      >
                                        <Download className="w-3 h-3" /> Download
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* OUTSTATION CONVEYANCE */}
                  {dRow.approval_type === 'outstation_conveyance' && (
                    <div className="space-y-3" data-testid="detail-outstation-conveyance">
                      {/* PDF Action Banner */}
                      <div className="flex items-center justify-between p-3.5 rounded-xl bg-blue-50/80 border border-blue-200">
                        <div>
                          <p className="text-xs font-bold text-blue-900">Outstation Travel Request</p>
                          <p className="text-[11px] text-blue-700 font-mono mt-0.5">
                            {dItem.request_number || dItem.id || dRow.request_number || '—'}
                          </p>
                        </div>
                        <button
                          type="button"
                          data-testid="detail-pdf-btn-outstation"
                          onClick={() => downloadConveyancePdf(dRow, dItem)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-xs transition"
                        >
                          <FileDown className="w-3.5 h-3.5" /> Download PDF
                        </button>
                      </div>

                      {/* Travel Details & Itinerary */}
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-1">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-blue-600" /> Travel Details & Itinerary
                        </p>
                        <DetailRow label="Request Number" value={dItem.request_number || dItem.id || '—'} />
                        <DetailRow label="Travel Purpose" value={dItem.travel_purpose || dItem.purpose || '—'} />
                        <DetailRow label="Travel Type" value={(dItem.travel_type || dItem.conveyance_category || '').toUpperCase() || 'OUTSTATION'} />
                        <DetailRow label="Travel Dates" value={dItem.travel_dates || `${dItem.start_date || ''} → ${dItem.end_date || ''}`} />
                        <DetailRow label="Cities / Route" value={dItem.route || `${dItem.origin_city || ''} → ${dItem.destination_city || ''}`} />
                        <DetailRow label="Travel Mode" value={dItem.travel_mode || '—'} />
                        {dItem.journey_details && Array.isArray(dItem.journey_details) && dItem.journey_details.length > 0 && (
                          <div className="py-1.5 border-b border-slate-50">
                            <span className="text-xs text-slate-500 block mb-1">Journey Segments:</span>
                            <div className="space-y-1">
                              {dItem.journey_details.map((seg, sIdx) => (
                                <div key={sIdx} className="text-[11px] bg-slate-200/70 p-1.5 rounded flex justify-between">
                                  <span>{seg.from_city || seg.origin} → {seg.to_city || seg.destination}</span>
                                  <span className="text-slate-500">{seg.date || seg.departure_date} · {seg.mode}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Itemized Expense Categories (for Self Booking / Reimbursement claims) */}
                      {(dItem.is_self_booking || dItem.self_booking_details) && (
                        <div className="bg-slate-50 rounded-xl p-4 border border-purple-100 space-y-2">
                          <p className="text-[10px] font-bold uppercase tracking-wide text-purple-700 mb-1 flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5 text-purple-600" /> Itemized Expense Category Breakdown
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                            <div className="p-2 bg-white rounded-lg border border-slate-200">
                              <span className="text-[10px] text-slate-500 block">Ticket & Travel</span>
                              <span className="font-bold font-mono text-slate-800">
                                {fmtAmt(dItem.self_booking_details?.ticket_amount ?? 0)}
                              </span>
                            </div>
                            <div className="p-2 bg-white rounded-lg border border-slate-200">
                              <span className="text-[10px] text-slate-500 block">Hotel & Stay</span>
                              <span className="font-bold font-mono text-slate-800">
                                {fmtAmt(dItem.self_booking_details?.hotel_amount ?? 0)}
                              </span>
                            </div>
                            <div className="p-2 bg-white rounded-lg border border-slate-200">
                              <span className="text-[10px] text-slate-500 block">Food & Meals</span>
                              <span className="font-bold font-mono text-slate-800">
                                {fmtAmt(dItem.self_booking_details?.food_amount ?? 0)}
                              </span>
                            </div>
                            <div className="p-2 bg-white rounded-lg border border-slate-200">
                              <span className="text-[10px] text-slate-500 block">Local Conveyance</span>
                              <span className="font-bold font-mono text-slate-800">
                                {fmtAmt(dItem.self_booking_details?.local_travel_amount ?? 0)}
                              </span>
                            </div>
                            <div className="p-2 bg-white rounded-lg border border-slate-200">
                              <span className="text-[10px] text-slate-500 block">Miscellaneous</span>
                              <span className="font-bold font-mono text-slate-800">
                                {fmtAmt(dItem.self_booking_details?.misc_amount ?? 0)}
                              </span>
                            </div>
                            <div className="p-2 bg-purple-50 rounded-lg border border-purple-200">
                              <span className="text-[10px] text-purple-700 font-bold block">Total Claimed</span>
                              <span className="font-bold font-mono text-purple-900">
                                {fmtAmt(dItem.total_amount ?? dItem.claimed_amount ?? dItem.self_booking_details?.total_claim_amount ?? 0)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Budget & Advance Information */}
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-1">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5">
                          <IndianRupee className="w-3.5 h-3.5 text-emerald-600" /> Budget & Advances
                        </p>
                        <DetailRow label="Estimated Budget" value={fmtAmt(dItem.estimated_budget)} />
                        <DetailRow label="Requested Advance" value={fmtAmt(dItem.requested_advance_amount ?? dItem.advance_requested_amount ?? dItem.advance_details?.total_advance ?? 0)} />
                        <DetailRow label="Approved Advance Amount" value={dItem.approved_advance_amount !== null && dItem.approved_advance_amount !== undefined ? fmtAmt(dItem.approved_advance_amount) : 'Pending Approval'} />
                        <DetailRow label="Total Claimed / Budget Amount" value={fmtAmt(dItem.total_amount ?? dItem.approved_amount ?? dItem.amount)} />
                      </div>

                      {/* Passengers & Booking Preferences */}
                      {(dItem.passengers || dItem.booking_preferences) && (
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-1">
                          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">Booking & Passengers</p>
                          {dItem.passengers && <DetailRow label="Passengers" value={typeof dItem.passengers === 'string' ? dItem.passengers : JSON.stringify(dItem.passengers)} />}
                          {dItem.booking_preferences && <DetailRow label="Preferences" value={typeof dItem.booking_preferences === 'string' ? dItem.booking_preferences : JSON.stringify(dItem.booking_preferences)} />}
                        </div>
                      )}

                      {/* Dedicated Attachments Card */}
                      <div className="bg-white rounded-xl p-4 border border-slate-200 space-y-2 shadow-xs" data-testid="conveyance-attachments-card">
                        <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-700 flex items-center gap-1.5">
                            <Paperclip className="w-3.5 h-3.5 text-blue-600" /> Supporting Attachments
                            <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] bg-blue-100 text-blue-700 font-semibold">
                              {(dItem.attachments || []).length}
                            </span>
                          </p>
                        </div>
                        {(!dItem.attachments || dItem.attachments.length === 0) ? (
                          <p className="text-xs text-slate-400 italic py-1">No supporting attachments uploaded</p>
                        ) : (
                          <div className="divide-y divide-slate-100">
                            {dItem.attachments.map((att, idx) => {
                              const attName = att.name || `Attachment #${idx + 1}`;
                              const attType = (att.type || att.name?.split('.').pop() || 'FILE').toUpperCase();
                              const attSize = att.size ? `${Math.round(att.size / 1024)} KB` : '';
                              return (
                                <div key={idx} className="flex items-center justify-between py-2 gap-2">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="p-1.5 rounded-lg bg-blue-50 text-blue-600 shrink-0">
                                      <FileText className="w-4 h-4" />
                                    </span>
                                    <div className="min-w-0">
                                      <p className="text-xs font-semibold text-slate-800 truncate" title={attName}>{attName}</p>
                                      <div className="flex items-center gap-2 text-[10px] text-slate-400">
                                        <span className="font-mono bg-slate-100 px-1 rounded">{attType}</span>
                                        {attSize && <span>{attSize}</span>}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0">
                                    <button
                                      type="button"
                                      data-testid={`view-attachment-${idx}`}
                                      onClick={() => viewAttachment(att)}
                                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition"
                                    >
                                      <Eye className="w-3 h-3" /> View
                                    </button>
                                    <button
                                      type="button"
                                      data-testid={`download-attachment-${idx}`}
                                      onClick={() => downloadAttachment(att, idx)}
                                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded transition"
                                    >
                                      <Download className="w-3 h-3" /> Download
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Approval history / audit trail */}
                  {(detail.data?.history || []).length > 0 && (
                    <div data-testid="detail-history">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1">
                        <History className="w-3.5 h-3.5" /> Approval Trail
                      </p>
                      <div className="space-y-1.5">
                        {detail.data.history.map((h, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs bg-slate-50 rounded-lg px-3 py-2">
                            <span className="font-semibold text-slate-700">{h.by_name || h.approver_name || h.level_name || 'System'}</span>
                            <span className="text-slate-500">{h.action || h.status}</span>
                            {(h.remarks) && <span className="text-slate-400 italic">“{h.remarks}”</span>}
                            <span className="ml-auto text-slate-400 whitespace-nowrap">{(h.at || h.decided_at || '').slice(0, 10)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Action Footer — Fixed at bottom, never scrolls off */}
            {!detailLoading && isPendingRow && (
              <div className="shrink-0 bg-slate-50 border-t border-slate-200 px-5 sm:px-6 py-3.5 z-10 space-y-3" data-testid="detail-action-panel">
                {dRow.approval_type === 'local_conveyance' && (() => {
                  const lc = getLocalConveyanceDetails(dItem, localConveyanceKm, isAmountOverride, localConveyanceOverrideAmount);
                  return (
                    <div className="flex items-center justify-between bg-blue-50/90 border border-blue-200/80 rounded-lg px-3 py-2 text-xs">
                      <span className="text-slate-600 font-medium">Claim Amount to Approve:</span>
                      <span className="font-bold text-blue-700 text-sm">
                        ₹{lc.finalAmount.toFixed(2)}
                        <span className="text-[11px] font-normal text-slate-500 ml-1.5">
                          ({isAmountOverride ? 'Explicit Override' : (lc.isKmChanged ? `Recalculated: ${lc.currentKm} KM @ ₹${lc.rate.toFixed(2)}/KM` : 'Policy Calculated')})
                        </span>
                      </span>
                    </div>
                  );
                })()}

                {AMOUNT_EDIT_TYPES.includes(dRow.approval_type) && (dItem.allow_amount_edit !== false) && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Approved Amount (leave blank to keep {fmtAmt(dItem.total_amount ?? dItem.amount)})
                    </label>
                    <div className="relative">
                      <IndianRupee className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
                      <input type="number" min="0" data-testid="approved-amount-input"
                        value={approvedAmount}
                        onChange={(e) => setApprovedAmount(e.target.value)}
                        placeholder={`${dItem.total_amount ?? dItem.amount ?? ''}`}
                        className="w-full border-2 border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:border-[#0052FF] bg-white" />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Remarks</label>
                  <textarea data-testid="approval-remarks-input" rows={2}
                    value={remarks} onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Required for Reject / Send Back"
                    className="w-full border-2 border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[#0052FF] bg-white" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <button data-testid="action-approve" disabled={acting}
                    onClick={() => act(dRow, 'approve')}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition cursor-pointer">
                    <CheckCircle className="w-4 h-4" /> Approve
                  </button>
                  <button data-testid="action-reject" disabled={acting}
                    onClick={() => act(dRow, 'reject')}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-bold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition cursor-pointer">
                    <XCircle className="w-4 h-4" /> Reject
                  </button>
                  <button data-testid="action-sendback" disabled={acting}
                    onClick={() => act(dRow, 'rework')}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-bold bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 transition cursor-pointer">
                    <RotateCcw className="w-4 h-4" /> Send Back
                  </button>
                </div>
              </div>
            )}
            {!detailLoading && !isPendingRow && (
              <div className="shrink-0 bg-slate-50 border-t border-slate-200 px-5 sm:px-6 py-3 flex justify-end z-10">
                <button
                  type="button"
                  onClick={() => setDetail(null)}
                  className="px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 transition cursor-pointer"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerApprovalInbox;
